import { useEffect, useState } from 'react';
import { getKidSub, useKidToken } from '@/auth/authStore';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { LmsAssignmentResponse } from '../lms/LmsAssignmentResponse';
interface Plan {
  id: string;
  title: string;
  subject_key: string;
  version: number;
  starts_on: string;
  weeks: number;
  objectives: string[];
  tasks: Array<{
    key: string;
    week: number;
    title: string;
    instructions: string;
    due_at: string;
    assignment_id: string | null;
    available?: boolean;
    opens_at?: string;
  }>;
}
export function IndividualLearningPlans({
  subjectKey,
  request = api,
  kidId,
}: {
  subjectKey?: string;
  request?: typeof api;
  kidId?: string;
}) {
  useKidToken();
  const learner = kidId ?? getKidSub();
  const plans = useQuery({
    queryKey: ['individual-learning-plans', learner],
    queryFn: () => request<{ items: Plan[] }>('/study/learning-plans'),
    retry: false,
  });
  const [opened, setOpened] = useState<string | null>(null);
  useEffect(() => setOpened(null), [learner]);
  const rows = (plans.data?.items ?? []).filter((p) => !subjectKey || p.subject_key === subjectKey);
  if (!rows.length) return null;
  return (
    <section
      aria-label="My individual learning plans"
      className="mt-8 rounded-3xl border border-hairline bg-canvas-pure p-5 sm:p-7 space-y-5"
    >
      <h2 className="text-xl font-extrabold text-ink">My learning plan</h2>
      <p className="text-sm text-slate2">
        Read your goals, open a weekly task and submit your work. Your teacher will give feedback;
        submitting work does not automatically mean a goal is mastered.
      </p>
      {rows.map((p) => (
        <article key={p.id} className="space-y-3">
          <h3 className="text-lg font-bold">{p.title}</h3>
          <p className="text-sm text-slate2">
            {p.subject_key} · {p.weeks} weeks from {p.starts_on} · published version {p.version}
          </p>
          <ul className="list-disc pl-5">
            {p.objectives.map((o, i) => (
              <li key={i}>{o}</li>
            ))}
          </ul>
          <ol className="space-y-3">
            {p.tasks.map((t) => (
              <li className="rounded-2xl border border-hairline p-4" key={t.key}>
                <h4 className="font-bold">
                  Week {t.week}: {t.title}
                </h4>
                <p className="whitespace-pre-wrap my-2">{t.instructions}</p>
                <p className="text-sm text-slate2">Due {new Date(t.due_at).toLocaleString()}</p>
                {t.assignment_id && t.available !== false ? (
                  <div className="flex flex-wrap items-center gap-3 mt-3">
                    <button
                      className="rounded-full bg-brand-sky px-5 py-2 font-bold text-white"
                      onClick={() => setOpened(opened === t.assignment_id ? null : t.assignment_id)}
                    >
                      {opened === t.assignment_id ? 'Close task' : 'Open task and feedback'}
                    </button>
                    {request === api && (
                      <a
                        className="btn-pill-ghost inline-flex"
                        href={`/learn/study/learning-plans/tasks/${encodeURIComponent(t.assignment_id)}`}
                      >
                        Open task page
                      </a>
                    )}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-slate2">
                    Scheduled ·{' '}
                    {t.opens_at
                      ? `opens ${new Date(t.opens_at).toLocaleString()}`
                      : 'your teacher will open this task in its scheduled week'}
                  </p>
                )}
                {t.assignment_id && opened === t.assignment_id && (
                  <LmsAssignmentResponse
                    assignmentId={t.assignment_id}
                    request={request}
                    kidId={kidId}
                  />
                )}
              </li>
            ))}
          </ol>
        </article>
      ))}
    </section>
  );
}
